import React, { useState } from "react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Film, Clapperboard, Share2, Copy, Sparkles, Loader2, RotateCw, Clock, History, Star, Music, Upload, X } from "lucide-react";

function MusicSection({ project, onChange }) {
  const [busy, setBusy] = useState(false);
  const customId = project.settings?.custom_music_media_id;
  const customName = project.settings?.custom_music_name || "Custom track";
  const mood = project.settings?.music;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/projects/${project.id}/music/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Custom music added — re-render to hear it in your reel");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Music upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/projects/${project.id}/music`);
      toast.success("Removed — back to the built-in music");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-4" data-testid="music-section">
      <div className="flex items-center gap-2 mb-1">
        <Music className="w-4 h-4 text-amber-500" />
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-amber-500">Background Music</span>
      </div>
      <p className="text-[11px] text-zinc-600 mb-3">
        Upload your own track (MP3, M4A or WAV, up to 20MB). It replaces the built-in music and is looped/trimmed to your reel, mixed under the narration. Re-render to apply.
      </p>
      {customId ? (
        <div className="flex items-center justify-between gap-3 border border-emerald-500/40 bg-emerald-500/10 rounded-sm px-3 py-2" data-testid="custom-music-active">
          <div className="flex items-center gap-2 min-w-0">
            <Music className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="text-sm text-emerald-200 truncate" data-testid="custom-music-name">{customName}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <audio controls src={mediaUrl(customId)} className="h-8 max-w-[200px]" data-testid="custom-music-preview" />
            <Button data-testid="remove-music-button" onClick={remove} disabled={busy} size="sm" variant="outline"
              className="h-8 rounded-sm border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <label className="inline-flex">
            <input type="file" accept="audio/*,.mp3,.m4a,.wav,.aac" className="hidden" onChange={upload} data-testid="music-upload-input" disabled={busy} />
            <span className={`h-9 px-4 inline-flex items-center gap-2 rounded-sm border border-zinc-700 text-sm text-zinc-200 hover:bg-zinc-800 cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid="music-upload-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload track
            </span>
          </label>
          <span className="text-xs text-zinc-500">
            {mood && mood !== "none" ? `Currently using the built-in “${mood}” music.` : "No background music yet."}
          </span>
        </div>
      )}
    </div>
  );
}

const MODEL_LABELS = { mini: "Seedance 2.0 Mini", fast: "Seedance 2.0 Fast", pro: "Seedance 2.0 Pro", v25: "Seedance 2.5", veo: "Veo 3.1" };
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};
const snapLabel = (s = {}) =>
  [s.resolution && `${s.resolution}p`, s.aspect, MODEL_LABELS[s.model] || s.model, s.caption_size && `captions ${s.caption_size}`]
    .filter(Boolean).join(" · ");

export default function FinalPanel({ project, onGoToTab, onChange }) {
  const final = project.final;
  const [social, setSocial] = useState(project.social || null);
  const [genning, setGenning] = useState(false);
  const [rerendering, setRerendering] = useState(false);
  const [exporting, setExporting] = useState("");
  const [auto, setAuto] = useState(!!project.settings?.auto_rerender);
  const [savingAuto, setSavingAuto] = useState(false);
  const [restoring, setRestoring] = useState("");
  const [featured, setFeatured] = useState(!!project.featured);
  const [savingFeature, setSavingFeature] = useState(false);

  const isRendering = project.pipeline?.render === "running" || project.render_progress != null;

  const toggleFeature = async () => {
    const nv = !featured;
    setFeatured(nv);
    setSavingFeature(true);
    try {
      await api.post(`/projects/${project.id}/feature`, { featured: nv });
      toast.success(nv ? "Featured — this reel now shows on your public site" : "Removed from the public site");
    } catch (err) {
      setFeatured(!nv);
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update");
    } finally {
      setSavingFeature(false);
    }
  };

  const toggleAuto = async () => {
    const nv = !auto;
    setAuto(nv);
    setSavingAuto(true);
    try {
      await api.post(`/projects/${project.id}/settings`, { auto_rerender: nv });
      toast.success(nv ? "Auto re-render on — your final updates automatically after edits" : "Auto re-render off");
    } catch (err) {
      setAuto(!nv);
      toast.error("Could not update setting");
    } finally {
      setSavingAuto(false);
    }
  };

  const restore = async (mid) => {
    setRestoring(mid);
    try {
      await api.post(`/projects/${project.id}/render-history/${mid}/restore`);
      toast.success("Restored that version");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRestoring("");
    }
  };

  const reRender = async () => {
    setRerendering(true);
    try {
      await api.post(`/projects/${project.id}/clips/confirm`);
      toast.success("Re-rendering your video with the latest settings…");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setRerendering(false);
    }
  };

  if (!final || !final.final_media_id) {
    if (isRendering) {
      const pct = Math.max(0, Math.min(99, project.render_progress ?? 0));
      return (
        <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-10" data-testid="final-rendering">
          <div className="flex items-center gap-3 text-amber-500 mb-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-mono2 text-[11px] uppercase tracking-[0.2em]" data-testid="render-phase">
              {project.render_phase || "Rendering your reel…"}
            </span>
          </div>
          <p className="text-zinc-400 text-sm mb-5">
            Hang tight — we're compositing your final video. The first render can take a
            little longer while the renderer warms up.
          </p>
          <div className="h-2.5 w-full bg-zinc-800 rounded-full overflow-hidden" data-testid="render-progress-track">
            <div
              className="h-full bg-amber-500 transition-all duration-700 ease-out"
              style={{ width: `${pct}%` }}
              data-testid="render-progress-bar"
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="font-mono2 text-[11px] text-amber-400" data-testid="render-progress-pct">{pct}%</span>
            <span className="font-mono2 text-[10px] text-zinc-600 uppercase tracking-widest">
              {project.settings?.resolution}p · {project.settings?.aspect}
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center" data-testid="final-empty">
          <Film className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">Your finished reel will appear here after rendering.</p>
          <Button onClick={() => onGoToTab && onGoToTab("clips")} variant="ghost" className="mt-4 rounded-sm text-amber-400">
            View clip progress
          </Button>
        </div>
        <MusicSection project={project} onChange={onChange} />
      </div>
    );
  }

  const src = mediaUrl(final.final_media_id);
  const vertical = project.settings?.aspect === "9:16";
  // Use the app's canonical public origin (same host that serves the API) so the
  // link is correct even when the app is viewed through an embedded/preview wrapper
  // where window.location.origin would be the wrong host.
  const publicOrigin = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
  const shareUrl = `${publicOrigin}/r/${project.id}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const genCaption = async () => {
    setGenning(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/caption`);
      setSocial(data);
      toast.success("Caption ready");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setGenning(false);
    }
  };

  const exports = project.exports || {};
  const exportFormat = async (aspect) => {
    setExporting(aspect);
    try {
      await api.post(`/projects/${project.id}/export`, { aspect });
      toast.success(`Rendering ${aspect} version… it'll appear here shortly.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setTimeout(() => setExporting(""), 1200);
    }
  };
  const captionText = social ? `${social.caption || ""}\n\n${(social.hashtags || []).join(" ")}`.trim() : "";

  return (
    <div className="fade-up" data-testid="final-panel">
      {(final.stale || isRendering) && (
        <div className="mb-4 flex items-center justify-between gap-3 border border-amber-500/40 bg-amber-500/10 rounded-sm px-4 py-3" data-testid="final-stale-banner">
          <div className="flex items-center gap-2 text-amber-300 text-sm">
            {isRendering ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <RotateCw className="w-4 h-4 shrink-0" />}
            <span>
              {isRendering
                ? `Re-rendering your video… ${Math.max(0, Math.min(99, project.render_progress ?? 0))}%`
                : "This version is outdated — re-render to apply your latest changes. Your current video stays available below until then."}
            </span>
          </div>
          {!isRendering && (
            <Button data-testid="stale-rerender-button" onClick={reRender} disabled={rerendering} size="sm"
              className="h-8 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 shrink-0">
              {rerendering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Re-render now"}
            </Button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-amber-500">
            <Clapperboard className="w-4 h-4" />
            <span className="font-mono2 text-[10px] uppercase tracking-[0.2em]">
              Final Cut · {final.engine} · {final.duration ? `${final.duration.toFixed(1)}s` : ""}
            </span>
          </div>
          {[final.has_voice && "Voiceover", final.has_music && "Music", final.has_outro && "Branded Outro"]
            .filter(Boolean)
            .map((t) => (
              <span key={t} className="font-mono2 text-[9px] uppercase tracking-widest px-2 py-1 border border-zinc-700 text-zinc-400 rounded-sm">
                {t}
              </span>
            ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="auto-rerender-toggle"
            onClick={toggleAuto}
            disabled={savingAuto}
            title="Automatically re-render after you make edits"
            className={`h-10 px-3 rounded-sm border text-xs flex items-center gap-2 transition-colors disabled:opacity-50 ${auto ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10" : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"}`}
          >
            <span className={`w-8 h-4 rounded-full relative transition-colors ${auto ? "bg-emerald-500" : "bg-zinc-700"}`}>
              <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${auto ? "left-4" : "left-0.5"}`} />
            </span>
            Auto re-render
          </button>
          <Button data-testid="rerender-button" onClick={reRender} disabled={rerendering} variant="outline"
            className="h-10 rounded-sm border-zinc-700 text-zinc-200 hover:bg-zinc-800 disabled:opacity-50">
            {rerendering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCw className="w-4 h-4 mr-2" />} Re-render
          </Button>
          <Button data-testid="share-button" onClick={() => copy(shareUrl, "Share link")} variant="outline"
            className="h-10 rounded-sm border-zinc-700 text-zinc-200 hover:bg-zinc-800">
            <Share2 className="w-4 h-4 mr-2" /> Copy Share Link
          </Button>
          <button
            data-testid="feature-toggle"
            onClick={toggleFeature}
            disabled={savingFeature}
            title="Show this reel on your public landing page"
            className={`h-10 px-3 rounded-sm border text-xs flex items-center gap-2 transition-colors disabled:opacity-50 ${featured ? "border-amber-500/60 text-amber-300 bg-amber-500/10" : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"}`}
          >
            {savingFeature ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className={`w-4 h-4 ${featured ? "fill-amber-400 text-amber-400" : ""}`} />}
            {featured ? "Featured on site" : "Feature on site"}
          </button>
          <a href={src} download={`${(project.final?.title || "property-reel").replace(/\s+/g, "-")}.mp4`}>
            <Button data-testid="download-button" className="h-10 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors">
              <Download className="w-4 h-4 mr-2" /> Download MP4
            </Button>
          </a>
        </div>
      </div>

      <div className="flex justify-center bg-black border border-zinc-800 rounded-sm p-6">
        <video data-testid="final-video" src={src} controls autoPlay loop
          className={`rounded-sm ${vertical ? "max-h-[70vh]" : "w-full max-w-3xl"}`} />
      </div>

      <div className="mt-4 flex items-center gap-2 text-sm text-zinc-400" data-testid="share-link-row">
        <Share2 className="w-4 h-4 text-amber-500" />
        <a href={shareUrl} target="_blank" rel="noreferrer" className="text-amber-400 hover:underline truncate">{shareUrl}</a>
        <button onClick={() => copy(shareUrl, "Link")} className="text-zinc-500 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
        <span className="text-zinc-600 text-xs">· public page, no login needed</span>
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500" data-testid="render-timestamp">
        <Clock className="w-3.5 h-3.5 text-zinc-600" />
        <span>Rendered {final.rendered_at ? fmtDate(final.rendered_at) : "—"}</span>
        {snapLabel(final.settings_snapshot) && (
          <span className="text-zinc-600">· {snapLabel(final.settings_snapshot)}</span>
        )}
      </div>

      {(project.render_history || []).filter((h) => h.final_media_id !== final.final_media_id).length > 0 && (
        <div className="mt-6 border border-zinc-800 bg-zinc-900/40 rounded-sm p-4" data-testid="render-history">
          <div className="flex items-center gap-2 mb-1">
            <History className="w-4 h-4 text-amber-500" />
            <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-amber-500">Render history</span>
          </div>
          <p className="text-[11px] text-zinc-600 mb-3">Your last few renders — download any, or restore one to make it the current version.</p>
          <div className="space-y-2">
            {(project.render_history || [])
              .filter((h) => h.final_media_id !== final.final_media_id)
              .map((h) => (
                <div key={h.final_media_id} className="flex items-center justify-between gap-3 border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-950/60" data-testid={`history-${h.final_media_id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-200 flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-zinc-600 shrink-0" /> {fmtDate(h.rendered_at)}
                      {h.duration ? <span className="text-zinc-600 text-xs">· {h.duration.toFixed(1)}s</span> : null}
                    </div>
                    <div className="text-[11px] text-zinc-600 truncate">{snapLabel(h.settings_snapshot) || h.engine}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a href={mediaUrl(h.final_media_id)} download={`reel-${(h.rendered_at || "").slice(0, 10)}.mp4`}>
                      <Button data-testid={`history-download-${h.final_media_id}`} size="sm" variant="outline" className="h-8 rounded-sm border-zinc-700 text-zinc-200 hover:bg-zinc-800">
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                    <Button data-testid={`history-restore-${h.final_media_id}`} onClick={() => restore(h.final_media_id)} disabled={restoring === h.final_media_id} size="sm"
                      className="h-8 rounded-sm bg-zinc-800 text-amber-300 hover:bg-zinc-700 disabled:opacity-50">
                      {restoring === h.final_media_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RotateCw className="w-3.5 h-3.5 mr-1" /> Restore</>}
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <MusicSection project={project} onChange={onChange} />
      </div>

      <div className="mt-6 border border-zinc-800 bg-zinc-900/40 rounded-sm p-4" data-testid="export-formats">
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-amber-500">Export for every platform</span>
        <p className="text-[11px] text-zinc-600 mt-1 mb-3">Render this reel in another shape — same clips, captions and title.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { a: "9:16", label: "Reels / TikTok", sub: "9:16 vertical" },
            { a: "1:1", label: "Instagram Feed", sub: "1:1 square" },
            { a: "16:9", label: "YouTube / MLS", sub: "16:9 wide" },
          ].map((f) => {
            const ex = exports[f.a];
            const rendering = exporting === f.a || ex?.status === "rendering";
            return (
              <div key={f.a} className="border border-zinc-800 rounded-sm p-3 bg-zinc-950/60" data-testid={`export-${f.a}`}>
                <div className="font-medium text-sm text-zinc-100">{f.label}</div>
                <div className="font-mono2 text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{f.sub}</div>
                {ex?.status === "done" && ex.final_media_id ? (
                  <a href={mediaUrl(ex.final_media_id)} download={`reel-${f.a.replace(":", "x")}.mp4`}>
                    <Button data-testid={`export-download-${f.a}`} size="sm" className="w-full h-9 rounded-sm bg-emerald-500 text-zinc-950 font-semibold hover:bg-emerald-400">
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                    </Button>
                  </a>
                ) : ex?.status === "failed" ? (
                  <Button data-testid={`export-retry-${f.a}`} onClick={() => exportFormat(f.a)} size="sm" variant="outline"
                    className="w-full h-9 rounded-sm border-red-500/40 text-red-400 hover:bg-red-500/10">
                    Failed — retry
                  </Button>
                ) : (
                  <Button data-testid={`export-run-${f.a}`} onClick={() => exportFormat(f.a)} disabled={rendering} size="sm" variant="outline"
                    className="w-full h-9 rounded-sm border-zinc-700 text-zinc-200 hover:bg-zinc-800 disabled:opacity-50">
                    {rendering ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Rendering…</> : <><Film className="w-3.5 h-3.5 mr-1.5" /> Render</>}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 border border-zinc-800 bg-zinc-900/40 rounded-sm p-4" data-testid="social-caption">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-amber-500">Social Caption</span>
          <div className="flex gap-2">
            <Button data-testid="gen-caption-button" onClick={genCaption} disabled={genning} size="sm"
              className="h-8 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 disabled:opacity-50">
              {genning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> {social ? "Regenerate" : "Write caption"}</>}
            </Button>
            {social && (
              <Button data-testid="copy-caption-button" onClick={() => copy(captionText, "Caption")} size="sm" variant="outline"
                className="h-8 rounded-sm border-zinc-700 text-zinc-200 hover:bg-zinc-800">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
            )}
          </div>
        </div>
        {social ? (
          <div>
            <p className="text-sm text-zinc-200 whitespace-pre-line">{social.caption}</p>
            <p className="mt-2 text-sm text-sky-400/90">{(social.hashtags || []).join(" ")}</p>
          </div>
        ) : (
          <p className="text-sm text-zinc-500">Generate a ready-to-post Instagram/TikTok caption with hashtags for this listing.</p>
        )}
      </div>
    </div>
  );
}
